'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import Image from 'next/image';
import {
  LayoutDashboard,
  MessageSquare,
  Globe,
  Menu,
  X,
  Home,
  LogOut,
  Lightbulb,
  CreditCard,
  HelpCircle,
  Search,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/clients';
import { LanguageSelector } from '@/components/ui/LanguageSelector';
import styles from './MobileBottomNav.module.css';

interface MobileBottomNavProps {
  locale: string;
  email?: string;
  isAuthenticated?: boolean;
}

/**
 * Global Bottom Navigation Bar for Mobile Devices
 *
 * Follows mobile UX best practices:
 * - Thumb Zone Optimization: Bottom navigation for easy reach
 * - Fitts's Law: 48x48px minimum touch targets
 * - Recognition over Recall: Icons + labels
 * - Progressive Enhancement: Only shown on mobile (<769px)
 *
 * Features:
 * - Fixed position at bottom with safe-area-inset
 * - 4 primary navigation items (Reports, Chat, Audit, Menu)
 * - Menu drawer with logo, Home, Reports, Language, Sign Out
 * - Hide on scroll down, show on scroll up
 * - Works for both authenticated and non-authenticated users
 * - Accessible with ARIA labels
 */
export function MobileBottomNav({ locale, email, isAuthenticated = false }: MobileBottomNavProps) {
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('navigation');
  const supabase = createClient();
  const [showMenu, setShowMenu] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);
  const scrollThreshold = 10;

  // Close menu when route changes
  useEffect(() => {
    setShowMenu(false);
  }, [pathname]);

  // Hide on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      // Don't hide when menu is open
      if (showMenu) {
        setIsHidden(false);
        return;
      }

      const currentScrollY = window.scrollY;
      const scrollDelta = currentScrollY - lastScrollY.current;

      // Only trigger if scroll distance exceeds threshold
      if (Math.abs(scrollDelta) < scrollThreshold) {
        return;
      }

      // At top of page - always show
      if (currentScrollY < 50) {
        setIsHidden(false);
      }
      // Scrolling down - hide
      else if (scrollDelta > 0) {
        setIsHidden(true);
      }
      // Scrolling up - show
      else {
        setIsHidden(false);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showMenu]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (showMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showMenu]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    router.push(`/${locale}`);
    router.refresh();
  };

  // Check active states
  const isHomeActive = pathname === `/${locale}` || pathname === `/${locale}/`;
  const isDashboardActive = pathname === `/${locale}/dashboard`;
  const isChatActive = pathname === `/${locale}/chat`;
  const isAuditActive = pathname === `/${locale}/audit` || pathname.includes('/dashboard/audit');

  return (
    <>
      <nav
        className={`${styles.bottomNav} ${isHidden ? styles.bottomNavHidden : ''}`}
        role="navigation"
        aria-label={t('mobileNavigation')}
      >
        {/* Logo */}
        <Link
          href={`/${locale}`}
          className={styles.navLogo}
          aria-label="Interface Audit - Home"
        >
          <Image
            src="/logo-mobile.svg"
            alt=""
            width={28}
            height={28}
            className={styles.navLogoImage}
          />
        </Link>

        {/* Chat - Links to public chat page */}
        <Link
          href={`/${locale}/chat`}
          className={`${styles.navItem} ${isChatActive ? styles.active : ''}`}
          aria-label={t('chat')}
          aria-current={isChatActive ? 'page' : undefined}
        >
          <MessageSquare size={24} className={styles.navIcon} />
          {/* Short variant: the full label overflows a 4-tab bar */}
          <span className={styles.navLabel}>{t('chatMobile')}</span>
        </Link>

        {/* Audit - Public audit page for unauthenticated, dashboard for authenticated */}
        <Link
          href={isAuthenticated ? `/${locale}/dashboard/audit` : `/${locale}/audit`}
          className={`${styles.navItem} ${isAuditActive ? styles.active : ''}`}
          aria-label={t('audit')}
          aria-current={isAuditActive ? 'page' : undefined}
        >
          <Globe size={24} className={styles.navIcon} />
          <span className={styles.navLabel}>{t('audit')}</span>
        </Link>

        {/* Menu Button */}
        <button
          type="button"
          onClick={() => setShowMenu(true)}
          className={styles.navItem}
          aria-label={t('menu')}
          aria-expanded={showMenu}
        >
          <Menu size={24} className={styles.navIcon} />
          <span className={styles.navLabel}>{t('menu')}</span>
        </button>
      </nav>

      {/* Menu Drawer */}
      {showMenu && (
        <>
          {/* Backdrop */}
          <div
            className={styles.menuBackdrop}
            onClick={() => setShowMenu(false)}
            aria-hidden="true"
          />

          {/* Drawer */}
          <div className={styles.menuDrawer}>
            {/* Header with User Info and Close Button */}
            <div className={styles.menuHeader}>
              {/* User Info - Only show if authenticated */}
              {isAuthenticated && email && (
                <div className={styles.userInfo}>
                  <div className={styles.userAvatar}>
                    {email.charAt(0).toUpperCase()}
                  </div>
                  <span className={styles.userEmail}>{email}</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowMenu(false)}
                className={styles.closeButton}
                aria-label={t('closeMenu')}
              >
                <X size={24} />
              </button>
            </div>

            {/* Navigation Links */}
            <div className={styles.menuSection}>
              <Link
                href={`/${locale}`}
                className={`${styles.menuItem} ${isHomeActive ? styles.menuItemActive : ''}`}
                onClick={() => setShowMenu(false)}
              >
                <Home size={20} />
                <span>{t('home')}</span>
              </Link>
              <Link
                href={`/${locale}/audit`}
                className={`${styles.menuItem} ${isAuditActive ? styles.menuItemActive : ''}`}
                onClick={() => setShowMenu(false)}
              >
                <Search size={20} />
                <span>{t('freeAudit')}</span>
              </Link>
              {isAuthenticated && (
                <Link
                  href={`/${locale}/dashboard`}
                  className={`${styles.menuItem} ${isDashboardActive ? styles.menuItemActive : ''}`}
                  onClick={() => setShowMenu(false)}
                >
                  <LayoutDashboard size={20} />
                  <span>{t('dashboard')}</span>
                </Link>
              )}
              <Link
                href={`/${locale}#how-it-works`}
                className={styles.menuItem}
                onClick={() => setShowMenu(false)}
              >
                <Lightbulb size={20} />
                <span>{t('howItWorks')}</span>
              </Link>
              <Link
                href={`/${locale}#pricing`}
                className={styles.menuItem}
                onClick={() => setShowMenu(false)}
              >
                <CreditCard size={20} />
                <span>{t('pricing')}</span>
              </Link>
              <Link
                href={`/${locale}#faq`}
                className={styles.menuItem}
                onClick={() => setShowMenu(false)}
              >
                <HelpCircle size={20} />
                <span>{t('faq')}</span>
              </Link>
            </div>

            {/* Language Section */}
            <div className={styles.menuSection}>
              <div className={styles.menuSectionLabel}>{t('language')}</div>
              <LanguageSelector className={styles.languageSelector} />
            </div>

            {/* Sign Out - Only show if authenticated */}
            {isAuthenticated && (
              <div className={styles.menuSection}>
                <button
                  type="button"
                  onClick={handleLogout}
                  className={styles.signOutButton}
                >
                  <LogOut size={20} />
                  <span>{t('signOut')}</span>
                </button>
              </div>
            )}

            {/* Sign In - Only show if not authenticated */}
            {!isAuthenticated && (
              <div className={styles.menuSection}>
                <Link
                  href={`/${locale}/sign-in`}
                  className={styles.signInButton}
                  onClick={() => setShowMenu(false)}
                >
                  <span>{t('signIn')}</span>
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
